/**
 * Diagnóstico de ABASTOVIRTUAL-2Z4 / -25N (#5): Inertia arranca por
 * back/forward y resuelve un componente llamado "undefined" aunque los guards
 * de `historyState.ts` ya descartan los states ajenos. Tres parches después no
 * sabemos QUÉ hay en `history.state` cuando pasa, así que esto lo manda a
 * Sentry como contexto del error.
 *
 * Sólo la forma del state, nunca su contenido: el state de Inertia trae los
 * props de la página (carrito, usuario, direcciones). Claves, tipos, el nombre
 * de componente y el path sin query alcanzan para distinguir las hipótesis
 * (state ajeno, state de Inertia mutilado, restauración desde bfcache).
 */

export type StateShape = {
    type: string;
    keys?: string[];
    component?: string;
    url_path?: string;
    has_props?: boolean;
    has_version?: boolean;
};

const MAX_KEYS = 15;
const MAX_COMPONENT = 60;

export function describeState(state: unknown): StateShape {
    if (state === null) {
        return { type: 'null' };
    }

    if (typeof state !== 'object') {
        return { type: typeof state };
    }

    const s = state as Record<string, unknown>;
    const shape: StateShape = {
        type: Array.isArray(state) ? 'array' : 'object',
        keys: Object.keys(s).slice(0, MAX_KEYS),
        component: typeof s.component === 'string'
            ? JSON.stringify(s.component.slice(0, MAX_COMPONENT))
            : `<${typeof s.component}>`,
        has_props: typeof s.props === 'object' && s.props !== null,
        has_version: 'version' in s,
    };

    if (typeof s.url === 'string') {
        shape.url_path = s.url.split(/[?#]/)[0].slice(0, 120);
    }

    return shape;
}

type GuardEvent = { at: string; state: StateShape };

const guardLog: GuardEvent[] = [];
let restoredFromBfcache = false;

/** Lo que hicieron los guards en esta carga, para cruzarlo con el error. */
export function noteGuard(at: string, state: unknown): void {
    guardLog.push({ at, state: describeState(state) });
}

/** `pageshow.persisted` = la página volvió desde bfcache sin re-ejecutar scripts. */
export function trackBfcache(target: Window): void {
    target.addEventListener('pageshow', (e) => {
        if ((e as PageTransitionEvent).persisted) {
            restoredFromBfcache = true;
        }
    });
}

function navigationType(target: Window): string | undefined {
    const entry = target.performance?.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;

    return entry?.type;
}

export function missingPageContext(target: Window, name: string): Record<string, unknown> {
    return {
        requested_component: JSON.stringify(String(name).slice(0, MAX_COMPONENT)),
        navigation_type: navigationType(target),
        restored_from_bfcache: restoredFromBfcache,
        history_length: target.history.length,
        history_state: describeState(target.history.state),
        guards: guardLog.slice(-5),
    };
}

type SentryLike = { setContext(name: string, ctx: Record<string, unknown>): void };

/**
 * Deja el contexto en Sentry antes de que el error salga. Sentry se carga
 * aparte (`js/pack/sentry.js`, async): si todavía no está, tampoco va a
 * capturar el error, así que no hay nada que perder.
 */
export function reportMissingPage(target: Window, name: string): void {
    const sentry = (target as unknown as { Sentry?: SentryLike }).Sentry;

    sentry?.setContext('inertia_history', missingPageContext(target, name));
}

/** Sólo para tests. */
export function resetHistoryDiagnostics(): void {
    guardLog.length = 0;
    restoredFromBfcache = false;
}
